import { normalizeEmail } from '@yoyoclub/shared';
import {
  signIn,
  signUp,
  signOut,
  getCurrentUser,
  fetchAuthSession,
  confirmSignUp,
  resendSignUpCode,
  resetPassword,
  confirmResetPassword,
  signInWithRedirect,
  autoSignIn,
  type SignInInput,
  type SignUpInput,
  type ConfirmSignUpInput,
  type ResetPasswordInput,
  type ConfirmResetPasswordInput,
} from 'aws-amplify/auth';
import { createContext, useContext, useState, useEffect, useCallback } from 'react';

import { rememberRedirect } from '../utils/redirectAfterLogin';

export interface AuthUser {
  userId: string; // Cognito sub
  username: string; // Cognito username
  email?: string;
  name?: string;
  role?: string; // custom:role attribute (fallback)
  groups?: string[]; // cognito:groups from JWT
  isAdmin?: boolean; // Helper flag
  isHost?: boolean; // Helper flag
}

export interface AuthContextValue {
  user: AuthUser | null;
  loading: boolean;
  isAuthenticated: boolean;
  login: (
    email: string,
    password: string
  ) => Promise<{ success: boolean; requiresConfirmation?: boolean }>;
  loginWithGoogle: (redirectTo?: string | null) => Promise<void>;
  register: (
    email: string,
    password: string,
    name: string
  ) => Promise<{ success: boolean; userId: string | undefined }>;
  confirmRegistration: (email: string, code: string) => Promise<{ autoSignInStep?: string }>;
  resendConfirmationCode: (email: string) => Promise<void>;
  requestPasswordReset: (email: string) => Promise<void>;
  confirmPasswordReset: (email: string, code: string, newPassword: string) => Promise<void>;
  logout: () => Promise<void>;
  getToken: () => Promise<string | undefined>;
  refreshUser: () => Promise<void>;
}

export const AuthContext = createContext<AuthContextValue | null>(null);

export function useAuthProvider() {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [loading, setLoading] = useState(true);

  /**
   * Check current authentication status
   */
  const checkUser = useCallback(async (forceRefresh = false) => {
    try {
      const currentUser = await getCurrentUser();
      const session = await fetchAuthSession({ forceRefresh });
      const idToken = session.tokens?.idToken;

      if (idToken) {
        const groups = (idToken.payload['cognito:groups'] as string[]) || [];
        const isAdmin = groups.includes('Admins');
        const isHost = groups.includes('Hosts');

        setUser({
          userId: currentUser.userId,
          username: currentUser.username,
          email: idToken.payload.email as string,
          name: idToken.payload.name as string,
          role: idToken.payload['custom:role'] as string,
          groups,
          isAdmin,
          isHost,
        });
      }
    } catch {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkUser();
  }, [checkUser]);

  /**
   * Login user with email and password
   */
  const login = useCallback(
    async (email: string, password: string) => {
      try {
        const result = await signIn({
          username: normalizeEmail(email),
          password,
        } as SignInInput);

        if (result.isSignedIn) {
          await checkUser();
          return { success: true };
        }

        // Handle additional steps (MFA, new password required, etc.)
        if (result.nextStep.signInStep === 'CONFIRM_SIGN_UP') {
          return { success: false, requiresConfirmation: true };
        }

        return { success: false };
      } catch (error) {
        throw new Error(error instanceof Error ? error.message : 'Login failed');
      }
    },
    [checkUser]
  );

  /**
   * Login with Google OAuth.
   *
   * Optionally remembers a post-login redirect target across the OAuth
   * round-trip. Always clears any previously-stashed redirect first so a
   * stale value from an abandoned/failed earlier flow doesn't leak through.
   */
  const loginWithGoogle = useCallback(async (redirectTo?: string | null) => {
    // Clear any stale redirect (and persist a fresh one if provided).
    // This must happen for every OAuth entry point — Login, Register, etc. —
    // so the App-level Hub callback never consumes a stale target.
    rememberRedirect(redirectTo ?? null);
    try {
      await signInWithRedirect({ provider: 'Google' });
    } catch (error) {
      throw new Error(error instanceof Error ? error.message : 'Google login failed');
    }
  }, []);

  /**
   * Register new user
   */
  const register = useCallback(async (email: string, password: string, name: string) => {
    // Cognito is case-sensitive on email: the canonical lowercase form must be
    // the only one that ever reaches it
    const canonicalEmail = normalizeEmail(email);
    const result = await signUp({
      username: canonicalEmail,
      password,
      options: {
        userAttributes: {
          email: canonicalEmail,
          name,
        },
        autoSignIn: true,
      },
    } as SignUpInput);

    return {
      success: true,
      userId: result.userId,
    };
  }, []);

  /**
   * Confirm registration with verification code
   * Returns autoSignInStep if auto sign-in is available
   */
  const confirmRegistration = useCallback(
    async (email: string, code: string) => {
      const result = await confirmSignUp({
        username: normalizeEmail(email),
        confirmationCode: code,
      } as ConfirmSignUpInput);

      // If autoSignIn was enabled during signUp, complete it
      if (result.nextStep?.signUpStep === 'COMPLETE_AUTO_SIGN_IN') {
        try {
          const signInResult = await autoSignIn();
          if (signInResult.isSignedIn) {
            await checkUser();
            return { autoSignInStep: 'SIGNED_IN' };
          }
        } catch {
          // autoSignIn failed — fallback to manual login
        }
      }

      return {};
    },
    [checkUser]
  );

  /**
   * Resend confirmation code
   */
  const resendConfirmationCode = useCallback(async (email: string) => {
    await resendSignUpCode({
      username: normalizeEmail(email),
    });
  }, []);

  /**
   * Request password reset - sends reset code to email
   */
  const requestPasswordReset = useCallback(async (email: string) => {
    try {
      await resetPassword({
        username: normalizeEmail(email),
      } as ResetPasswordInput);
    } catch (error) {
      throw new Error(error instanceof Error ? error.message : 'Failed to request password reset');
    }
  }, []);

  /**
   * Confirm password reset with code and new password
   */
  const confirmPasswordReset = useCallback(
    async (email: string, code: string, newPassword: string) => {
      try {
        await confirmResetPassword({
          username: normalizeEmail(email),
          confirmationCode: code,
          newPassword,
        } as ConfirmResetPasswordInput);
      } catch (error) {
        throw new Error(error instanceof Error ? error.message : 'Failed to reset password');
      }
    },
    []
  );

  /**
   * Logout user
   */
  const logout = useCallback(async () => {
    try {
      await signOut();
      setUser(null);
    } catch {
      throw new Error('Logout failed');
    }
  }, []);

  /**
   * Get current ID token
   */
  const getToken = useCallback(async () => {
    try {
      const session = await fetchAuthSession();
      return session.tokens?.idToken?.toString();
    } catch {
      return undefined;
    }
  }, []);

  /**
   * Refresh user data
   */
  const refreshUser = useCallback(async () => {
    await checkUser(true);
  }, [checkUser]);

  return {
    user,
    loading,
    isAuthenticated: !!user,
    login,
    loginWithGoogle,
    register,
    confirmRegistration,
    resendConfirmationCode,
    requestPasswordReset,
    confirmPasswordReset,
    logout,
    getToken,
    refreshUser,
  };
}

/**
 * Hook to access auth context
 * Must be used within AuthProvider
 */
export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

// Re-export the type for backwards compatibility
export type UseAuthReturn = AuthContextValue;
